import React from 'react';

import { getBwaReport, getProfitAndLossReport } from '../../../api';
import { logError } from '../../../logging/loggingService';
import { buildCsvString, downloadCsv, formatCurrency, formatDateInput, safeNumber } from './financialReportFormatters';

export default function useFinancialReportData({ hasAccounting }) {
    const { startOfYear, endOfYear } = React.useMemo(() => {
        const now = new Date();
        return {
            startOfYear: new Date(now.getFullYear(), 0, 1),
            endOfYear: new Date(now.getFullYear(), 11, 31),
        };
    }, []);

    const initialFilters = React.useMemo(
        () => ({
            from: formatDateInput(startOfYear),
            to: formatDateInput(endOfYear),
            costCenters: [],
        }),
        [startOfYear, endOfYear]
    );

    const [mode, setMode] = React.useState('bwa');
    const [filters, setFilters] = React.useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = React.useState(initialFilters);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [expandedPeriods, setExpandedPeriods] = React.useState(() => new Set());
    const [expandedAccounts, setExpandedAccounts] = React.useState(() => new Set());

    const fetchReport = React.useCallback(
        async (currentFilters) => {
            if (!hasAccounting) {
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const payload = {
                    from: currentFilters.from || null,
                    to: currentFilters.to || null,
                    costCenters: Array.isArray(currentFilters.costCenters) ? currentFilters.costCenters : [],
                };
                const apiCall = mode === 'bwa' ? getBwaReport : getProfitAndLossReport;
                const res = await apiCall(payload);
                setData(res.data);
            } catch (err) {
                logError('Fehler beim Laden des Reports', err);
                setError('Report konnte nicht geladen werden.');
            } finally {
                setLoading(false);
            }
        },
        [mode, hasAccounting]
    );

    React.useEffect(() => {
        fetchReport(appliedFilters);
    }, [fetchReport, appliedFilters]);

    const handleModeChange = (value) => {
        setMode(value);
        setExpandedPeriods(new Set());
        setExpandedAccounts(new Set());
    };

    const handleFromChange = (date) => {
        setFilters((prev) => ({ ...prev, from: date }));
    };

    const handleToChange = (date) => {
        setFilters((prev) => ({ ...prev, to: date }));
    };

    const handleCostCentersChange = (value) => {
        setFilters((prev) => ({ ...prev, costCenters: value }));
    };

    const handleApplyFilters = () => {
        setExpandedPeriods(new Set());
        setExpandedAccounts(new Set());
        setAppliedFilters({ ...filters, costCenters: [...filters.costCenters] });
    };

    const togglePeriod = (key) => {
        setExpandedPeriods((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleAccount = (periodKey, accountKey) => {
        const composite = `${periodKey}::${accountKey}`;
        setExpandedAccounts((prev) => {
            const next = new Set(prev);
            if (next.has(composite)) {
                next.delete(composite);
            } else {
                next.add(composite);
            }
            return next;
        });
    };

    const chartSeries = React.useMemo(() => {
        if (!data?.periods?.length) {
            return [];
        }
        return data.periods.map((period) => {
            const totals = period.totals || {};
            const income = safeNumber(totals.income);
            const expense = -safeNumber(totals.expense);
            const result = safeNumber(totals.result);
            return {
                key: period.key,
                label: period.label,
                income,
                expense,
                result,
            };
        });
    }, [data]);

    const chartWidth = React.useMemo(() => {
        const base = 560;
        if (!chartSeries.length) {
            return base;
        }
        return Math.max(base, 160 * (chartSeries.length - 1) + 320);
    }, [chartSeries]);

    const availableCostCenters = React.useMemo(() => {
        const list = data?.availableCostCenters || [];
        if (!Array.isArray(list)) {
            return [];
        }
        return [...new Set([...(filters.costCenters || []), ...list])];
    }, [data, filters.costCenters]);

    const formattedTotals = React.useMemo(() => {
        const totals = data?.totals || {};
        return {
            income: formatCurrency(safeNumber(totals.income)),
            expense: formatCurrency(-safeNumber(totals.expense)),
            result: formatCurrency(safeNumber(totals.result)),
        };
    }, [data]);

    const handleExport = () => {
        if (!data?.periods?.length) {
            return;
        }
        const rows = [['Periode', 'Konto', 'Art', 'Beleg', 'Datum', 'Beschreibung', 'Betrag', 'Kostenstelle']];
        data.periods.forEach((period) => {
            const label = period.label || period.key;
            const totals = period.totals || {};
            rows.push([label, '', 'Summe', '', '', '', safeNumber(totals.result), '']);
            (period.accounts || []).forEach((account) => {
                const accountLabel = [account.accountNumber, account.accountName].filter(Boolean).join(' ');
                const sign = account.nature === 'EXPENSE' ? -1 : 1;
                rows.push([label, accountLabel, account.nature, '', '', '', sign * safeNumber(account.total), '']);
                (account.documents || []).forEach((doc) => {
                    rows.push([
                        label,
                        accountLabel,
                        'Beleg',
                        doc.number || '',
                        doc.date || '',
                        doc.description || '',
                        sign * safeNumber(doc.amount),
                        doc.costCenter || '',
                    ]);
                });
            });
        });
        const csv = buildCsvString(rows);
        const filename = `report-${mode}-${appliedFilters.from || 'start'}-${appliedFilters.to || 'end'}.csv`;
        downloadCsv(csv, filename);
    };

    return {
        mode,
        filters,
        data,
        loading,
        error,
        expandedPeriods,
        expandedAccounts,
        chartSeries,
        chartWidth,
        availableCostCenters,
        formattedTotals,
        handleModeChange,
        handleFromChange,
        handleToChange,
        handleCostCentersChange,
        handleApplyFilters,
        handleExport,
        togglePeriod,
        toggleAccount,
    };
}
